import OLMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import type { Layer } from 'ol/layer';
import { getConfig } from './config';
import { getLayerOlStyle } from './layer-styles';

const layersRegistry = new Map<string, Layer>();
const interactiveLayersRegistry = new Set<string>();

const layerStates = new Map<string, boolean>();   // id слоя → желаемая видимость
const groupStates = new Map<string, boolean>();   // id группы → желаемая видимость
const layerToGroup = new Map<string, string>();   // id слоя → id группы

function createLayer(layerConfig: any): Layer {
  const { id, type, source, hidden = false, interactive = false } = layerConfig;
  let layer: Layer;

  if (type === 'xyz') {
    layer = new TileLayer({
      source: new XYZ({
        url: source.url || '',
        attributions: source.attribution
      }),
      zIndex: 0
    });
  } 
  else if (type === 'wms') {
    const appConfig = getConfig('main');//(window as any).__APP_CONFIG__;
    const wmsUrl = appConfig?.geoserver?.wmsUrl;
    console.log('Layer WMS url: ', wmsUrl);
    
    // БЕРЕМ ИМЯ СЛОЯ НАПРЯМУЮ ИЗ КОНФИГУРАЦИИ
    // Используем geoserverLayer, с fallback на layerName или id на всякий случай
    const geoserverLayerName = source.geoserverLayer || source.layerName || id;

    layer = new TileLayer({
      source: new TileWMS({
        url: wmsUrl,
        params: {
          LAYERS: geoserverLayerName, // <-- Прямая подстановка идентификатора
          FORMAT: 'image/png',
          TRANSPARENT: true,
          TILED: true
        },
        serverType: 'geoserver'
      }),
      zIndex: 1
    });
  } else if (type === 'vector') {
    // === НОВЫЙ БЛОК: WFS-слой ===
    const appConfig = getConfig('main');//(window as any).__APP_CONFIG__;
    const olStyle = getLayerOlStyle(id);
    
    // Определяем источник данных
    let vectorSource: VectorSource;

    if (source.type === 'wfs') {
      // Вариант 1: Стандартный WFS через GeoServer
      const wfsUrl = appConfig?.geoserver?.wfsUrl || appConfig?.geoserver?.wmsUrl?.replace('/wms', '/wfs');
      const geoserverLayerName = source.geoserverLayer || source.layerName || id;

      vectorSource = new VectorSource({
        format: new GeoJSON(),
        url: function(extent) {
          return `${wfsUrl}?service=WFS&version=1.1.0&request=GetFeature` +
                 `&typename=${geoserverLayerName}` +
                 `&outputFormat=application/json` +
                 `&srsname=EPSG:3857` +
                 `&bbox=${extent.join(',')},EPSG:3857`;
        },
        strategy: bboxStrategy
      });
    } else {
      throw new Error(`Неизвестный тип источника векторного слоя: ${source.type}`);
    }
    layer = new VectorLayer({
      source: vectorSource,
      style: olStyle,
      zIndex: source.zIndex || 2
    });
    
    if (interactive) {
      interactiveLayersRegistry.add(id);
    }
  } 
  else {
    throw new Error(`Неизвестный тип слоя: ${type}`);
  }

  layer.set('id', id);
  layer.set('hidden', hidden ?? false);
  layer.setVisible(layerConfig.visible ?? true);
  layersRegistry.set(id, layer);

  return layer;
}

export function initLayers(map: OLMap): void {
  const layersConfig = getConfig('layers');
  
  if (!layersConfig?.groups) {
    console.warn('⚠️ Конфигурация слоёв не найдена');
    return;
  }

  // Сначала создаём все слои
  const processGroup = (group: any, parentGroupId?: string) => {
    if (group.layers) {
      group.layers.forEach((lc: any) => {
        const layer = createLayer(lc);
        map.getLayers().insertAt(0, layer);//addLayer(layer);
        
        // Запоминаем желаемое состояние слоя и его принадлежность группе
        layerStates.set(lc.id, lc.visible ?? true);
        if (parentGroupId) {
          layerToGroup.set(lc.id, parentGroupId);
        } else {
          layerToGroup.set(lc.id, group.id);
        }
      });
    }
    if (group.groups) {
      group.groups.forEach((sg: any) => processGroup(sg, group.id));
    }
  };

  layersConfig.groups.forEach(group => {
    // Инициализируем состояние группы (читаем из конфига, по умолчанию true)
    const groupVisible = group.visible !== undefined ? group.visible : true;
    groupStates.set(group.id, groupVisible);
    processGroup(group);
  });

  // Применяем эффективную видимость ко всем слоям
  applyVisibility();

  console.log('🗺️ Слои инициализированы. Интерактивных:', interactiveLayersRegistry.size);
}

function applyVisibility(): void {
  layerStates.forEach((_, layerId) => {
    const layer = getLayerById(layerId);
    if (layer) {
      layer.setVisible(getEffectiveVisibility(layerId));
    }
  });
}

// ============================================================================
// Публичные функции управления видимостью
// ============================================================================

/**
 * Получение желаемой видимости слоя (без учёта состояния группы)
 */
export function getLayerState(layerId: string): boolean {
  return layerStates.get(layerId) ?? true;
}

/**
 * Получение желаемой видимости группы
 */
export function getGroupVisibility(groupId: string): boolean {
  return groupStates.get(groupId) ?? true;
}

/**
 * Вычисление эффективной видимости слоя (с учётом состояния группы)
 */
export function getEffectiveVisibility(layerId: string): boolean {
  const layerVisible = layerStates.get(layerId) ?? true;
  const groupId = layerToGroup.get(layerId);
  
  if (!groupId) return layerVisible;
  
  const groupVisible = groupStates.get(groupId) ?? true;
  return layerVisible && groupVisible;
}

/**
 * Переключение видимости отдельного слоя
 */
export function setLayerVisibility(layerId: string, visible: boolean): void {
  layerStates.set(layerId, visible);
  applyVisibility();
}

/**
 * Переключение видимости группы целиком
 * НЕ МЕНЯЕТ индивидуальные состояния слоёв внутри группы
 */
export function setGroupVisibility(groupId: string, visible: boolean): void {
  groupStates.set(groupId, visible);
  applyVisibility();
}

/**
 * Получение состояния чекбокса группы на основе желаемых состояний слоёв
 */
export function getGroupCheckState(groupId: string): 'all' | 'none' | 'partial' {
  const layersConfig = getConfig('layers');
  if (!layersConfig) return 'none';

  const visibleLayerIds: string[] = [];
  
  const collectLayers = (g: any) => {
    if (g.layers) {
      g.layers.forEach((layer: any) => {
        if (!layer.hidden) {
          visibleLayerIds.push(layer.id);
        }
      });
    }
    if (g.groups) {
      g.groups.forEach((sg: any) => collectLayers(sg));
    }
  };

  // Находим группу
  for (const group of layersConfig.groups) {
    if (group.id === groupId) {
      collectLayers(group);
      break;
    }
  }

  if (visibleLayerIds.length === 0) return 'none';

  let checkedCount = 0;
  visibleLayerIds.forEach(layerId => {
    if (getLayerState(layerId)) checkedCount++;
  });

  if (checkedCount === 0) return 'none';
  if (checkedCount === visibleLayerIds.length) return 'all';
  return 'partial';
}

// ============================================================================
// Получение слоёв и интерактивность
// ============================================================================

export function getLayerById(layerId: string): Layer | null {
  return layersRegistry.get(layerId) || null;
}

export function getInteractiveLayerIds(): string[] {
  return Array.from(interactiveLayersRegistry);
}

export function isLayerInteractive(layerId: string): boolean {
  return interactiveLayersRegistry.has(layerId);
}